// src/Pages/GlobalUserInvite.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  Fade,
  Divider,
  Backdrop,
  Stack,
  CssBaseline,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { MonetizationOn, AccountBalance } from '@mui/icons-material';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { sha256 } from 'js-sha256';

import client from '../API';
import { niches, region } from '../Utils/constants';
import MuiPhoneInput from '../Components/MuiPhoneInput';
import { replaceConsoleError, replaceConsoleLog } from '../Utils/toastNotifications';
import AlertDialog from '../Components/AlertDialog';
import useAlertDialog from '../Components/useAlertDialog';

/* -----------------------------------------------------------------------------
 *  Light Theme Configuration - Locked regardless of user settings
 * -------------------------------------------------------------------------- */
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#667eea',
      light: '#98a7f2',
      dark: '#4f5dd1',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#764ba2',
      light: '#9c7bc7',
      dark: '#5a3d7a',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      color: '#1e293b',
    },
    h2: {
      fontWeight: 800,
      color: '#1e293b',
    },
    h3: {
      fontWeight: 700,
      color: '#1e293b',
    },
    h4: {
      fontWeight: 700,
      color: '#1e293b',
    },
    h5: {
      fontWeight: 600,
      color: '#1e293b',
    },
    h6: {
      fontWeight: 600,
      color: '#374151',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '12px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
});

/* -----------------------------------------------------------------------------
 *  Shared Styled Components
 * -------------------------------------------------------------------------- */
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
  padding: '14px 32px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '16px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: 'none',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(102, 126, 234, 0.6)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    transform: 'none',
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    transition: 'all 0.3s ease',
    border: '2px solid transparent',
    '&:hover': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#e2e8f0',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      border: '2px solid #667eea',
      '& fieldset': {
        borderColor: 'transparent',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: '#374151',
    '&.Mui-focused': {
      color: '#667eea',
    },
  },
}));

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  backgroundColor: 'rgba(15, 23, 42, 0.9)',
  backdropFilter: 'blur(8px)',
  zIndex: theme.zIndex.modal + 1,
}));

const ReferrerCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(4),
  borderRadius: '20px',
  boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
}));

const AccountTypeCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: selected ? '2px solid #667eea' : '2px solid #e2e8f0',
  borderRadius: '16px',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: selected ? '0 20px 40px rgba(102, 126, 234, 0.3)' : '0 20px 40px rgba(0, 0, 0, 0.1)',
    border: '2px solid #667eea',
  },
}));

/* -----------------------------------------------------------------------------
 *  Creator Flow (inline component) - Fixed callback dependencies
 * -------------------------------------------------------------------------- */
function GlobalCreatorInviteInline({ referrerData, onBack }) {
  const navigate = useNavigate();
  const { globalUserId } = useParams();
  const [searchParams] = useSearchParams();

  const inviteToken = searchParams.get('token');
  const claimToken =
    searchParams.get('claim_token') ||
    searchParams.get('claimToken') ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('claim_funds_token') : null);
  const prefilledUsername = searchParams.get('username') || '';

  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  // Creator form state
  const [username, setUsername] = useState(prefilledUsername);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [creatorRegion, setCreatorRegion] = useState('');
  const [creatorNiche, setCreatorNiche] = useState([]);
  const [platformHandles, setPlatformHandles] = useState({
    instagram: '',
    tiktok: '',
    youtube: '',
    podcast: '',
    twitch: '',
    kick: '',
    x: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const pkceRef = useRef(null);
  const tabOpen = useRef(false);
  const isWeb = Capacitor.getPlatform() === 'web';

  // Google OAuth constants (scoped here)
  const GOOGLE_IOS_CLIENT_ID =
    '489265257959-374fsc04dffos29tto4jsqcd2sch5f9o.apps.googleusercontent.com';
  const GOOGLE_SCOPES = [
    'https://mail.google.com/',
  ];
  const REDIRECT_URI =
    'com.googleusercontent.apps.489265257959-374fsc04dffos29tto4jsqcd2sch5f9o:/oauth2redirect';

  // Helpers
  const showSnackbar = useCallback((message, severity = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };
  
  const alertErr = (msg) => openDialog('Error', msg, closeDialog, closeDialog, 'Ok', null);

  // PKCE utils
  const base64url = (uint8) =>
    btoa(String.fromCharCode(...uint8)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  const createPKCE = () => {
    const rnd = crypto.getRandomValues(new Uint8Array(32));
    const verifier = base64url(rnd);
    const hash = sha256.arrayBuffer(verifier);
    const challenge = base64url(new Uint8Array(hash));
    return { verifier, challenge };
  };

  const exchangeCodeForIdToken = async (code, codeVerifier) => {
    const body = new URLSearchParams({
      code,
      client_id: GOOGLE_IOS_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      grant_type: 'authorization_code',
      code_verifier: codeVerifier,
    }).toString();

    const res = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error_description || 'Google token exchange failed');
    }
    const data = await res.json();
    if (!data.id_token) throw new Error('No id_token returned from Google');
    return data.id_token;
  };

  // Local validation - memoized to prevent recreating on every render
  const onSubmitLocalValidation = useCallback(() => {
    if (!termsAccepted) {
      showSnackbar('You must accept the Terms & Conditions to continue.');
      return false;
    }
    if (!username.trim()) {
      setValidationError('Username is required.');
      return false;
    }
    if (username.includes(' ')) {
      setValidationError('Creator name cannot contain spaces.');
      return false;
    }
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 9) {
      showSnackbar('You must enter a valid phone number.');
      return false;
    }
    if (!creatorRegion) {
      showSnackbar('Please select a region.');
      return false;
    }
    if (creatorNiche.length === 0) {
      showSnackbar('Please select at least one niche.');
      return false;
    }
    const anyHandle = Object.values(platformHandles).some((v) => v && String(v).trim().length > 0);
    if (!anyHandle) {
      showSnackbar('Enter at least one platform username.');
      return false;
    }
    setValidationError('');
    return true;
  }, [termsAccepted, username, phoneNumber, creatorRegion, creatorNiche, platformHandles, showSnackbar]);

  // Submit creator - memoized with proper dependencies
  const submitCreator = useCallback(async (googleToken) => {
    if (!onSubmitLocalValidation()) return;
    setLoading(true);

    const payload = {
      username: username.trim(),
      is_partner: false,
      region: creatorRegion,
      niche: creatorNiche,
      google_code: googleToken,
      phone_number: phoneNumber,
      youtube: platformHandles.youtube ? `https://www.youtube.com/@${platformHandles.youtube}` : null,
      instagram: platformHandles.instagram ? `https://www.instagram.com/${platformHandles.instagram}` : null,
      tiktok: platformHandles.tiktok ? `https://www.tiktok.com/@${platformHandles.tiktok}` : null,
      podcast: platformHandles.podcast || null,
      x: platformHandles.x ? `https://www.x.com/${platformHandles.x}` : null,
      twitch: platformHandles.twitch ? `https://www.twitch.tv/${platformHandles.twitch}` : null,
      kick: platformHandles.kick ? `https://www.kick.com/${platformHandles.kick}` : null,
      invite_token: inviteToken,
      referred_by: globalUserId,
    };
    if (claimToken) payload.claim_token = claimToken;

    try {
      const result = await client.globalUsers.claimCreatorInvite(payload);
      showSnackbar(result?.message || 'You have successfully created your CreatorConnect account.', 'success');
      const tokenFromResult = result?.claim_token || (result?.transfer && result.transfer.claim_token);
      const tokenToUse = tokenFromResult || claimToken;
      if (tokenToUse && typeof window !== 'undefined') {
        sessionStorage.setItem('claim_funds_token', tokenToUse);
      }
      setTimeout(() => {
        setLoading(false);
        if (tokenToUse) {
          const claimPath = `/claim-funds?token=${encodeURIComponent(tokenToUse)}`;
          navigate(`/login?tab=creator&redirect=${encodeURIComponent(claimPath)}`);
        } else if (result?.redirect_to) {
          navigate(result.redirect_to);
        } else {
          navigate('/login?tab=creator');
        }
      }, 1500);
    } catch (err) {
      replaceConsoleError('Account creation error', err);
      showSnackbar(
        `There was an error creating your account: ${err?.response?.data?.error || err.message}`
      );
      setLoading(false);
    }
  }, [
    onSubmitLocalValidation,
    username,
    creatorRegion,
    creatorNiche,
    phoneNumber,
    platformHandles,
    inviteToken,
    claimToken,
    globalUserId,
    showSnackbar,
    navigate
  ]);

  // Web Google flow - Fixed dependencies
  const webGoogleSuccess = useCallback((resp) => {
    replaceConsoleLog('Google success response', resp);
    const token = resp?.code || resp?.credential;
    if (token) {
      submitCreator(token);
    } else {
      showSnackbar('No authentication token received from Google');
    }
  }, [submitCreator, showSnackbar]);

  const webGoogleFail = useCallback((error) => {
    replaceConsoleError('Google OAuth error', error);
    showSnackbar(error?.error || 'Google sign-in failed. Please try again.');
  }, [showSnackbar]);

  // Native Google flow
  const nativeGoogle = async () => {
    try {
      pkceRef.current = createPKCE();
      const { challenge } = pkceRef.current;

      tabOpen.current = true;
      setLoading(true);

      await Browser.open({
        url: `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(
          GOOGLE_IOS_CLIENT_ID
        )}&redirect_uri=${encodeURIComponent(
          REDIRECT_URI
        )}&response_type=code&code_challenge=${encodeURIComponent(
          challenge
        )}&code_challenge_method=S256&scope=${encodeURIComponent(GOOGLE_SCOPES.join(' '))}&prompt=select_account`,
        presentationStyle: 'fullscreen',
      });
    } catch (err) {
      console.error('[GlobalCreatorInvite] Native Google launch error:', err);
      showSnackbar(err.message || 'Google login cancelled');
      setLoading(false);
      tabOpen.current = false;
    }
  };

  // Deep link handler (iOS app) - Fixed dependencies
  const handleDeepLink = useCallback(
    async ({ url }) => {
      try {
        const u = new URL(url);
        if (u.protocol.startsWith('com.googleusercontent.apps.') && u.pathname === '/oauth2redirect') {
          if (tabOpen.current) {
            await Browser.close().catch(() => {});
            tabOpen.current = false;
          }
          const code = u.searchParams.get('code');
          const error = u.searchParams.get('error');
          if (error) throw new Error(`Google OAuth error: ${error}`);
          if (!code) throw new Error('Missing OAuth code');

          const idToken = await exchangeCodeForIdToken(code, pkceRef.current?.verifier);
          await submitCreator(idToken);
        }
      } catch (err) {
        console.error('[GlobalCreatorInvite] Deep link error:', err);
        showSnackbar(err.message || 'Google login failed');
        setLoading(false);
      }
    },
    [submitCreator, showSnackbar] 
  );

  useEffect(() => {
    if (isWeb) return;
    const urlSub = App.addListener('appUrlOpen', handleDeepLink);
    const browserSub = Browser.addListener('browserFinished', () => {
      tabOpen.current = false;
      setLoading(false);
    });
    return () => {
      urlSub.remove();
      browserSub.remove();
    };
  }, [isWeb, handleDeepLink]);

  return (
    <Box>
      <StyledBackdrop open={loading}>
        <CircularProgress color="inherit" size={60} />
      </StyledBackdrop>

      <AlertDialog alertState={dialogState} />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            borderRadius: '12px',
            '&.MuiAlert-standardError': {
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
            },
            '&.MuiAlert-standardSuccess': {
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              color: '#166534',
            },
            '&.MuiAlert-standardWarning': {
              backgroundColor: '#fffbeb',
              border: '1px solid #fed7aa',
              color: '#92400e',
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Header for creator flow - only show if no referrer data */}
      {!referrerData && (
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
            Join Blitz as a Creator
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Monetize your content and connect with brands
          </Typography>
        </Box>
      )}

      {/* Form */}
      <Card sx={{ borderRadius: '20px', border: '1px solid #f1f5f9' }}>
        <CardContent sx={{ p: { xs: 3, md: 6 } }}>
          <form onSubmit={(e) => e.preventDefault()} noValidate>
            <Stack spacing={4}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>
                Creator Account Setup
              </Typography>

              <ModernTextField
                label="Username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                error={!!validationError}
                helperText={validationError || 'Use your main platform handle'}
                disabled={loading}
                fullWidth
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <ModernTextField
                  label="Region"
                  select
                  required
                  value={creatorRegion}
                  onChange={(e) => setCreatorRegion(e.target.value)}
                  disabled={loading}
                  fullWidth
                >
                  {region.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r === 'NACAUKAUS' ? 'USA, Canada, UK, Australia' : r}
                    </MenuItem>
                  ))}
                </ModernTextField>

                <ModernTextField
                  label="Niche"
                  select
                  required
                  value={creatorNiche}
                  SelectProps={{ multiple: true }}
                  onChange={(e) => setCreatorNiche(e.target.value)}
                  disabled={loading}
                  fullWidth
                >
                  {niches.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </ModernTextField>
              </Box>

              <Box sx={{ '& .react-tel-input': { width: '100%' } }}>
                <MuiPhoneInput
                  country="us"
                  preferredCountries={['us']}
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  required
                  inputProps={{
                    style: {
                      width: '100%',
                      borderRadius: '12px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      fontSize: '16px',
                      padding: '16px 14px',
                      transition: 'all 0.3s ease',
                    },
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                Platform Handles
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {[
                  ['youtube', '@YourChannelName'],
                  ['instagram', '@username'],
                  ['tiktok', '@yourusername'],
                  ['podcast', 'Podcast Name or URL'],
                  ['x', '@username'],
                  ['kick', '@username'],
                  ['twitch', '@username'],
                ].map(([field, hint]) => (
                  <ModernTextField
                    key={field}
                    label={`${field.charAt(0).toUpperCase() + field.slice(1)} Username`}
                    value={platformHandles[field]}
                    placeholder={hint}
                    onChange={(e) => setPlatformHandles((s) => ({ ...s, [field]: e.target.value }))}
                    disabled={loading}
                    fullWidth
                  />
                ))}
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={loading}
                    sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    I agree to the{' '}
                    <a
                      href="/terms_creators"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#667eea', textDecoration: 'none', fontWeight: 600 }}
                    >
                      Terms & Conditions
                    </a>
                  </Typography>
                }
              />

              {/* Google Sign-in CTA */}
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                {isWeb ? (
                  <GoogleLogin
                    size="large"
                    ux_mode="popup"
                    flow="auth-code"
                    access_type="offline"
                    scope={GOOGLE_SCOPES.join(' ')}
                    prompt="consent"
                    onSuccess={webGoogleSuccess}
                    onError={webGoogleFail}
                    disabled={!termsAccepted || loading}
                  />
                ) : (
                  <GradientButton 
                    onClick={nativeGoogle} 
                    disabled={!termsAccepted || loading} 
                    size="large" 
                    sx={{ py: 1.5, px: 6 }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} /> : null}
                    Sign up with Google
                  </GradientButton>
                )}
              </Box>

              {/* Back to selection button */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  pt: 2,
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <Button
                  onClick={onBack}
                  disabled={loading}
                  sx={{
                    borderRadius: '12px',
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Back to Selection
                </Button>
                <Box /> {/* Spacer for layout */}
              </Box>

              <Typography
                variant="body2"
                sx={{ textAlign: 'center', color: 'text.secondary', pt: 2, borderTop: '1px solid #f1f5f9' }}
              >
                By signing up, you'll be able to connect with brands and monetize your content.
              </Typography>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

/* -----------------------------------------------------------------------------
 *  Global User Invite (Brand only wizard + Creator inline flow switch)
 * -------------------------------------------------------------------------- */
const GlobalUserInvite = () => {
  const navigate = useNavigate();
  const { globalUserId } = useParams();
  const [searchParams] = useSearchParams();

  const accountType = searchParams.get('type'); // 'brand' or 'creator'
  const inviteToken = searchParams.get('token');
  const claimToken =
    searchParams.get('claim_token') ||
    searchParams.get('claimToken') ||
    (typeof window !== 'undefined' ? sessionStorage.getItem('claim_funds_token') : null);

  const [loading, setLoading] = useState(false);
  const [referrerData, setReferrerData] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState(accountType || '');
  const [showAccountSelection, setShowAccountSelection] = useState(!accountType);
  const [activeStep, setActiveStep] = useState(0);

  // Brand form state (creator is handled entirely by GlobalCreatorInviteInline)
  const [formData, setFormData] = useState({
    // Personal info
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    // Brand specific
    companyName: '',
    streetAddress: '',
    city: '',
    state: '',
    country: '',
    industry: '',
    bio: '',
    businessType: '',
    isAgency: '',
    monthlySpend: '',
    teamAccounts: '',
    paymentMethod: '',
    // Common
    termsAccepted: false,
    referralSource: '',
    referredBy: globalUserId,
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Static data for brand flow
  const industryOptions = [
    'Fashion & Beauty',
    'Technology',
    'Food & Beverage',
    'Health & Fitness',
    'Travel & Tourism',
    'Finance',
    'Education',
    'Entertainment',
    'Automotive',
    'Real Estate',
    'Hospitality',
    'Retail',
    'Manufacturing',
    'Marketing Agency',
    'E-commerce',
    'Gaming',
    'Sports',
    'Home & Garden',
    'Pets',
    'Other',
  ];
  const countryOptions = [
    'United States',
    'Canada',
    'United Kingdom',
    'Australia',
    'Germany',
    'France',
    'Italy',
    'Spain',
    'Japan',
    'India',
    'Brazil',
    'Mexico',
    'Netherlands',
    'Sweden',
    'Singapore',
    'South Korea',
    'Other',
  ];
  const stepsBrand = ['Personal Info', 'Company Details', 'Business Info'];

  // Fetch referrer data
  useEffect(() => {
    const fetchReferrerData = async () => {
      if (!globalUserId) return;
      try {
        setLoading(true);
        const data = await client.globalUsers.getPublicInfo(globalUserId);
        setReferrerData(data);
      } catch (error) {
        console.error('Error fetching referrer data:', error);
        setSnackbar({ open: true, message: 'Could not load referrer information', severity: 'warning' });
      } finally {
        setLoading(false);
      }
    };
    fetchReferrerData();
  }, [globalUserId]);

  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAccountTypeSelect = (type) => {
    setSelectedAccountType(type);
    setShowAccountSelection(false);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('type', type);
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const handleBackToSelection = () => {
    setShowAccountSelection(true);
    setSelectedAccountType('');
    setActiveStep(0);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('type');
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const validateStepBrand = (step) => {
    switch (step) {
      case 0:
        return (
          formData.firstName &&
          formData.lastName &&
          formData.email &&
          formData.password &&
          formData.confirmPassword &&
          formData.phoneNumber
        );
      case 1:
        return formData.companyName && formData.city && formData.country;
      case 2:
        return formData.termsAccepted;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (selectedAccountType === 'creator') return; // creator handled by inline component
    if (!validateStepBrand(activeStep)) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    if (activeStep === 0) {
      if (formData.password !== formData.confirmPassword) {
        showSnackbar('Passwords do not match', 'error');
        return;
      }
      if (formData.password.length < 6) {
        showSnackbar('Password must be at least 6 characters', 'error');
        return;
      }
    }
    if (activeStep === stepsBrand.length - 1) {
      handleSubmitBrand();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      setShowAccountSelection(true);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmitBrand = async () => {
    if (!formData.termsAccepted) {
      showSnackbar('Please accept the terms and conditions', 'error');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        accountType: 'brand',
        inviteToken,
        referredBy: globalUserId,
        ...formData,
      };
      if (claimToken) payload.claim_token = claimToken;
      const result = await client.globalUsers.claimInvite(payload);
      showSnackbar(result?.message || 'Account created successfully!', 'success');
      const tokenFromResult = result?.claim_token || (result?.transfer && result.transfer.claim_token);
      const tokenToUse = tokenFromResult || claimToken;
      if (tokenToUse && typeof window !== 'undefined') {
        sessionStorage.setItem('claim_funds_token', tokenToUse);
      }
      setTimeout(() => {
        setLoading(false);
        if (tokenToUse) {
          const claimPath = `/claim-funds?token=${encodeURIComponent(tokenToUse)}`;
          navigate(`/login?redirect=${encodeURIComponent(claimPath)}`);
        } else if (result?.redirect_to) {
          navigate(result.redirect_to);
        } else {
          navigate('/login');
        }
      }, 1500);
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage =
        error?.response?.data?.error || error?.error || error?.message || 'Registration failed';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderAccountSelection = () => (
    <Fade in={showAccountSelection}>
      <Box>
        <Typography variant="h3" sx={{ mb: 2, fontWeight: 800, textAlign: 'center' }}>
          Choose Your Account Type
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 6, textAlign: 'center' }}>
          Select the type of account you'd like to create
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <AccountTypeCard selected={false} onClick={() => handleAccountTypeSelect('brand')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <AccountBalance sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                  Brand Account
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                  Create campaigns, hire creators, and grow your brand with influencer marketing.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#e11d48' }}>
                  Payer Account: Funds campaigns (Money Out)
                </Typography>
              </CardContent>
            </AccountTypeCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <AccountTypeCard selected={false} onClick={() => handleAccountTypeSelect('creator')}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <MonetizationOn sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                  Creator Account
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                  Monetize your content, get hired by brands, and grow your creator business.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#15803d' }}>
                  Payee Account: Earn from campaigns (Money In)
                </Typography>
              </CardContent>
            </AccountTypeCard>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  const renderPersonalInfoStep = () => (
    <Box>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#1e293b' }}>
        Personal Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <ModernTextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12}>
          <ModernTextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12}>
          <ModernTextField fullWidth label="Phone Number" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            helperText="At least 6 characters"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderBrandStep2 = () => (
    <Box>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#1e293b' }}>
        Company Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ModernTextField fullWidth label="Company Name" name="companyName" value={formData.companyName} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12}>
          <ModernTextField fullWidth label="Street Address" name="streetAddress" value={formData.streetAddress} onChange={handleInputChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField fullWidth label="City" name="city" value={formData.city} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField fullWidth label="State/Province" name="state" value={formData.state} onChange={handleInputChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Country</InputLabel>
            <Select
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              label="Country"
              sx={{ borderRadius: '12px', backgroundColor: '#f8fafc' }}
            >
              {countryOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Industry</InputLabel>
            <Select
              name="industry"
              value={formData.industry}
              onChange={handleInputChange}
              label="Industry"
              sx={{ borderRadius: '12px', backgroundColor: '#f8fafc' }}
            >
              {industryOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <ModernTextField
            fullWidth
            label="Company Description"
            name="bio"
            multiline
            rows={4}
            value={formData.bio}
            onChange={handleInputChange}
            placeholder="Brief description of your company and what you do..."
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderBusinessInfoStep = () => (
    <Box>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#1e293b' }}>
        Business Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Business Structure</InputLabel>
            <Select
              name="businessType"
              value={formData.businessType}
              onChange={handleInputChange}
              label="Business Structure"
              sx={{ borderRadius: '12px', backgroundColor: '#f8fafc' }}
            >
              <MenuItem value="Single Entity">Single Company</MenuItem>
              <MenuItem value="Multiple Entities">Multiple Companies</MenuItem>
              <MenuItem value="Holding Company">Holding Company</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Are you an agency?</InputLabel>
            <Select
              name="isAgency"
              value={formData.isAgency}
              onChange={handleInputChange}
              label="Are you an agency?"
              sx={{ borderRadius: '12px', backgroundColor: '#f8fafc' }}
            >
              <MenuItem value="Yes">Yes, we're an agency</MenuItem>
              <MenuItem value="No">No, we're a brand</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Expected Monthly Spend ($)"
            name="monthlySpend"
            value={formData.monthlySpend}
            onChange={handleInputChange}
            placeholder="e.g., $5,000"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ModernTextField
            fullWidth
            label="Team Size"
            name="teamAccounts"
            value={formData.teamAccounts}
            onChange={handleInputChange}
            placeholder="How many team members will use this account?"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Preferred Payment Method</InputLabel>
            <Select
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleInputChange}
              label="Preferred Payment Method"
              sx={{ borderRadius: '12px', backgroundColor: '#f8fafc' }}
            >
              <MenuItem value="Credit Card">Credit Card</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer/ACH</MenuItem>
              <MenuItem value="PayPal">PayPal</MenuItem>
              <MenuItem value="Wire Transfer">Wire Transfer</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <ModernTextField
            fullWidth
            label="How did you hear about us?"
            name="referralSource"
            value={formData.referralSource}
            onChange={handleInputChange}
            placeholder="Google, friend referral, social media, etc."
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderBrandForm = () => (
    <Fade in={!showAccountSelection}>
      <Box>
        <Stepper activeStep={activeStep} sx={{ mb: 6 }}>
          {stepsBrand.map((label, index) => (
            <Step key={label}>
              <StepLabel>
                <Typography sx={{ fontWeight: 600, color: activeStep >= index ? '#1e293b' : '#94a3b8' }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card sx={{ borderRadius: '20px', border: '1px solid #f1f5f9' }}>
          <CardContent sx={{ p: { xs: 3, md: 6 } }}>
            {activeStep === 0 && renderPersonalInfoStep()}
            {activeStep === 1 && renderBrandStep2()}
            {activeStep === 2 && renderBusinessInfoStep()}

            {activeStep === stepsBrand.length - 1 && (
              <Box sx={{ mt: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="termsAccepted"
                      checked={formData.termsAccepted}
                      onChange={handleInputChange}
                      required
                      sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener" style={{ color: '#667eea', fontWeight: 600 }}>
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" target="_blank" rel="noopener" style={{ color: '#667eea', fontWeight: 600 }}>
                        Privacy Policy
                      </a>
                    </Typography>
                  }
                />
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 4,
                borderTop: '1px solid #f1f5f9',
                mt: 4,
              }}
            >
              <Button
                onClick={handleBack}
                disabled={loading}
                sx={{
                  borderRadius: '12px',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {activeStep === 0 ? 'Back to Selection' : 'Back'}
              </Button>
              <GradientButton onClick={handleNext} disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : activeStep === stepsBrand.length - 1 ? 'Create Account' : 'Continue'}
              </GradientButton>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );

  if (loading && !referrerData && !showAccountSelection && selectedAccountType !== 'creator') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography>Loading invite details...</Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          py: { xs: 4, md: 8 },
        }}
      >
        <Box maxWidth="lg" sx={{ mx: 'auto', px: { xs: 2, sm: 4 } }}>
          {/* Referrer Card - ONLY SHOWN ONCE at the top level */}
          {referrerData && (
            <ReferrerCard>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 2 }}>
                  <Avatar src={referrerData.avatar} sx={{ width: 80, height: 80, border: '3px solid white' }}>
                    {referrerData.name ? referrerData.name.charAt(0).toUpperCase() : 'U'}
                  </Avatar>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                      You're Invited!
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      {referrerData.name} invited you to join Blitz
                    </Typography>
                    {referrerData.company_name && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {referrerData.company_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Join thousands of creators and brands building successful partnerships
                </Typography>
              </CardContent>
            </ReferrerCard>
          )}

          {/* Header for non-referred users */}
          {!referrerData && (
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
                Join Blitz
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Create your account and start building partnerships
              </Typography>
            </Box>
          )}

          {/* Body */}
          {showAccountSelection
            ? renderAccountSelection()
            : selectedAccountType === 'creator'
            ? <GlobalCreatorInviteInline referrerData={referrerData} onBack={handleBackToSelection} />
            : renderBrandForm()}

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              severity={snackbar.severity}
              sx={{ 
                width: '100%', 
                borderRadius: '12px',
                '&.MuiAlert-standardError': {
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#991b1b'
                },
                '&.MuiAlert-standardSuccess': {
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  color: '#166534'
                },
                '&.MuiAlert-standardWarning': {
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fed7aa',
                  color: '#92400e'
                }
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default GlobalUserInvite;